import React from "react";
import { Paper, Typography, Box, Chip, Button } from "@mui/material";
import { motion } from "framer-motion";
import { CheckCircleOutline } from "@mui/icons-material";

export const PlanCard = ({
  plan,
  index = 0,
  isCompact = false,
  isAnnual = false,
  fromStartTrial = false,
  onCardClick,
  showButton = true,
  showDescription = true,
  country,
  currency,
  inSubscriptionPage = false,
}) => {
  // Helper function to format price display
  const formatPrice = () => {
    const price = country === "IN" ? plan?.price : plan?.price_dollar;
    const isCustomPlan = price === "Custom" || !price;

    if (isCustomPlan) {
      return "Custom";
    }

    const cleanPrice = String(price)
      .replace(/[₹$€£¥]/g, "")
      .replace(/\.?0+$/, "");

    return cleanPrice;
  };

  const displayPrice = formatPrice();
  const isCustomPlan = displayPrice === "Custom";

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      whileHover={{ y: isCompact ? 0 : -8, zindex: 99 }}
      style={{ height: "100%" }}
    >
      <Paper
        onClick={
          !isCompact && onCardClick ? (e) => onCardClick(plan, e) : undefined
        }
        elevation={isCompact ? 0 : plan?.popular_plan ? 4 : 1}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: isCompact ? "1rem" : "2rem",
          borderRadius: isCompact ? "0.5rem" : "1.5rem",
          backgroundColor: "white",
          border: plan?.popular_plan
            ? "1px solid #472ed1"
            : "1px solid #e2e8f0",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          minWidth: isCompact
            ? "200px"
            : { xs: "100%", sm: inSubscriptionPage ? "0" : "260px", md: "280px" },
          transformOrigin: "center center",
        }}
      >
        {plan?.popular_plan && !isCompact && (
          <Chip
            label="Most Popular"
            sx={{
              position: "absolute",
              top: -16,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#472ed1",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.7rem",
              zIndex: 1,
            }}
          />
        )}

        <Typography
          variant={isCompact ? "h6" : "h5"}
          sx={{
            fontWeight: 700,
            color: "primary.main",
            fontSize: isCompact ? "1.1rem" : "1.5rem",
            mb: isCompact ? "0.5rem" : "1rem",
            textAlign: "center",
          }}
        >
          {plan?.name}
        </Typography>

        <Box
          sx={{
            mb: isCompact ? "0.5rem" : "1.5rem",
            textAlign: "center",
            minHeight: isCompact ? "auto" : "60px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "center",
              gap: "0.1rem",
            }}
          >
            <Typography
              component="span"
              variant="body1"
              sx={{
                color: "#64748b",
                fontSize: isCompact ? "0.85rem" : "1rem",
                fontWeight: 500,
              }}
            >
              {!isCustomPlan ? currency : ""}
            </Typography>

            <Typography
              variant={isCompact ? "h6" : "h4"}
              sx={{
                fontWeight: 700,
                fontSize: isCompact ? "1.3rem" : "2rem",
                color: "#1e293b",
                lineHeight: 1,
              }}
            >
              {displayPrice}
            </Typography>

            {!isCustomPlan && (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: isCompact ? "0.75rem" : "0.8rem",
                  fontWeight: 400,
                  ml: "0.1rem",
                }}
              >
                {isAnnual ? "/yr" : "/mo"}
              </Typography>
            )}
          </Box>

          {isAnnual && plan?.discount_annual && !isCustomPlan && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                mt: "0.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.25rem",
                color: "#16a34a",
              }}
            >
              Save {plan.discount_annual}%
            </Typography>
          )}

          {isCustomPlan && (
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.85rem",
                mt: "0.25rem",
              fontStyle: "italic",
            }}
          >
              Contact For Pricing
            </Typography>
          )}
        </Box>

        {!isCompact && (
          <>
            {showDescription && (
              <Typography
                variant="body1"
                sx={{
                  mb: "1rem",
                  fontSize: "0.9rem",
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                {plan?.description}
              </Typography>
            )}

            <Box sx={{ mb: "2rem", flexGrow: 1 }}>
              {plan?.features?.slice(0, 5).map((feature, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: "0.75rem",
                  }}
                >
                  <CheckCircleOutline
                    sx={{
                      fontSize: "1.125rem",
                      mr: "0.75rem",
                      color: "primary.main",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "#334155", fontSize: "0.875rem" }}
                  >
                    {feature?.featureName}
                  </Typography>
                </Box>
              ))}
              {/* {plan?.features && plan.features.length > 5 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    textAlign: "center",
                    mt: 1,
                    fontWeight: 600,
                  }}
                >
                  +{plan.features.length - 5} more feature
                  {plan.features.length - 5 !== 1 ? "s" : ""}
                </Typography>
              )} */}
            </Box>
          </>
        )}

        {!isCompact && showButton && (
          <Button
            variant="contained"
            fullWidth
            sx={{ textTransform: "none" }}
            onClick={(e) => {
              if (plan.name !== "Enterprise" && onCardClick) {
                onCardClick(plan, e);
              }
            }}
          >
            {plan.name === "Enterprise"
              ? "Contact Sales"
              : fromStartTrial
                ? "Start Trial"
                : "Purchase"}
          </Button>
        )}
      </Paper>
    </motion.div>
  );
};
